import { Location } from '@angular/common';
import { Component, Input } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import {
  Tenant,
  TenantsService,
  UpdateTenantRequest,
} from 'src/app/services/tenants.service';
import { states } from 'src/app/utils/state-list';

@Component({
  selector: 'app-tenant-info-form',
  templateUrl: './tenant-info-form.component.html',
  styleUrls: ['./tenant-info-form.component.scss'],
})
export class TenantInfoFormComponent {
  @Input() tenant!: Tenant;

  first: string = '';
  last: string = '';
  title: string = '';
  company: string = '';

  address: string = '';
  address2: string = '';
  city: string = '';
  st: string = '';
  zip: string = '';

  billingSameAsAddress: boolean = false;
  billingAddress: string = '';
  billingAddress2: string = '';
  billingCity: string = '';
  billingSt: string = '';
  billingZip: string = '';

  phone1: string = '';
  phone2: string = '';
  emails: string[] = [];
  billingEmails: string[] = [];

  newEmail: string = '';
  newBillingEmail: string = '';

  phone1Error: string = '';
  phone2Error: string = '';
  emailError: string = '';
  billingEmailError: string = '';

  states = states;

  updating = false;
  updateSuccess = false;
  error = '';
  qboError = '';

  constructor(
    private authService: AuthService,
    private tenantsService: TenantsService,
    private location: Location
  ) {}

  ngOnInit(): void {
    console.log('tenant-info-form tenant: ', this.tenant);
    this.setTenantInfo(this.tenant);
  }

  isAdmin() {
    return this.authService.isAdmin();
  }

  setTenantInfo(info: Tenant) {
    this.first = info.first;
    this.last = info.last;
    this.title = info.title;
    this.company = info.company;
    this.address = info.address;
    this.address2 = info.address2;
    this.city = info.city;
    this.st = info.st;
    this.zip = info.zip;
    this.billingAddress = info.billing_address;
    this.billingAddress2 = info.billing_address2;
    this.billingCity = info.billing_city;
    this.billingSt = info.billing_st;
    this.billingZip = info.billing_zip;
    this.phone1 = info.phone1;
    this.phone2 = info.phone2;
    this.emails = info.emails;
    this.billingEmails = info.billing_emails || [];
    this.billingSameAsAddress = this.addressesMatch();
  }

  addressesMatch() {
    return (
      this.address === this.billingAddress &&
      this.address2 === this.billingAddress2 &&
      this.city === this.billingCity &&
      this.st === this.billingSt &&
      this.zip === this.billingZip
    );
  }

  addEmail() {
    if (!this.newEmail) {
      return;
    }

    if (!this.validateEmail(this.newEmail)) {
      this.emailError = 'Invalid email';
      return;
    }

    if (this.emails.includes(this.newEmail)) {
      this.emailError = 'Email already added';
      return;
    }

    this.emails.push(this.newEmail);
    this.newEmail = '';
    this.emailError = '';
  }

  removeEmail(email: string) {
    this.emails = this.emails.filter((e) => e !== email);
  }

  addBillingEmail() {
    if (!this.newBillingEmail) {
      return;
    }

    if (!this.validateEmail(this.newBillingEmail)) {
      this.billingEmailError = 'Invalid email';
      return;
    }

    if (this.billingEmails.includes(this.newBillingEmail)) {
      this.billingEmailError = 'Email already added';
      return;
    }

    this.billingEmails.push(this.newBillingEmail);
    this.newBillingEmail = '';
    this.billingEmailError = '';
  }

  removeBillingEmail(email: string) {
    this.billingEmails = this.billingEmails.filter((e) => e !== email);
  }

  handlePhoneChange(ev: Event, num: number) {
    const phone = (ev.target as HTMLInputElement).value;
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 0) {
      if (num === 1) {
        this.phone1 = '';
        this.phone1Error = '';
      } else {
        this.phone2 = '';
        this.phone2Error = '';
      }
      return;
    }

    if (digits.length !== 10) {
      console.error(new Error('Phone number must have 10 digits'));
      if (num === 1) {
        this.phone1Error = 'Phone number must have 10 digits';
      } else {
        this.phone2Error = 'Phone number must have 10 digits';
      }
      return;
    }

    const newPhone = `(${digits.slice(0, 3)}) ${digits.slice(
      3,
      6
    )}-${digits.slice(6)}`;

    if (num === 1) {
      this.phone1 = newPhone;
    } else {
      this.phone2 = newPhone;
    }
  }

  handleBillingSameAsAddressChecked(ev: Event) {
    const checked = (ev.target as HTMLInputElement).checked;
    if (checked) {
      this.billingAddress = this.address;
      this.billingAddress2 = this.address2;
      this.billingCity = this.city;
      this.billingSt = this.st;
      this.billingZip = this.zip;
    } else {
      this.billingAddress = '';
      this.billingAddress2 = '';
      this.billingCity = '';
      this.billingSt = '';
      this.billingZip = '';
    }
  }

  validateEmail(email: string) {
    console.log('validating email: ', email);
    // email regex
    const re = /\S+@\S+\.\S+/;
    if (!re.test(email)) {
      console.error(new Error('Invalid email'));
      return false;
    }

    return true;
  }

  submit() {
    console.log('submitting form');
    this.updating = true;
    this.updateSuccess = false;
    this.error = '';
    this.qboError = '';

    const request: UpdateTenantRequest = {
      first: this.first,
      last: this.last,
      title: this.title,
      company: this.company,
      address: this.address,
      address2: this.address2,
      city: this.city,
      st: this.st,
      zip: this.zip,
      phone1: this.phone1,
      phone2: this.phone2,
      emails: this.emails,
      billing_emails: this.billingEmails,
      billing_address: '',
      billing_address2: '',
      billing_city: '',
      billing_st: '',
      billing_zip: '',
    };

    if (this.billingSameAsAddress) {
      request.billing_address = this.address;
      request.billing_address2 = this.address2;
      request.billing_city = this.city;
      request.billing_st = this.st;
      request.billing_zip = this.zip;
    } else {
      request.billing_address = this.billingAddress;
      request.billing_address2 = this.billingAddress2;
      request.billing_city = this.billingCity;
      request.billing_st = this.billingSt;
      request.billing_zip = this.billingZip;
    }

    console.log('request: ', request);

    const tenantId = this.tenant.id;
    console.log('tenantId: ', tenantId);

    this.tenantsService.updateTenant(tenantId, request).subscribe({
      next: (response) => {
        console.log('response: ', response);
        this.updating = false;
        this.updateSuccess = true;

        if (this.isAdmin() && response.qbo_error) {
          if (
            typeof response.qbo_error === 'string' &&
            response.qbo_error.includes(
              'more than 100 characters in the email field'
            )
          ) {
            this.qboError =
              'Business email addresses are longer than 100 characters.';
          } else {
            this.qboError = response.qbo_error;
          }
        }
      },
      error: (error) => {
        this.updating = false;
        console.error(error);
        this.error = 'Error updating tenant information';
      },
    });
  }

  back() {
    this.location.back();
  }
}
